import { useEffect, useMemo, useState } from 'react'
import { Users, Search } from 'lucide-react'
import { apiGet } from '../admin/primitives.js'
import { Link, navigate } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { humanName } from './format.js'
import { useMenuPersonne } from './menu-personne.js'
import { accesLieDeLigne } from './menu-personne-modele.js'

/**
 * /equipe/rh/salaries — LES SALARIÉS (Lot 2.5, §45).
 * Langage MÉTIER uniquement : état RH, poste, matricule. Aucun statut
 * provider ici — les liaisons techniques vivent dans Administration →
 * Intégrations et dans la fiche (Permissions → détails techniques).
 */

interface PersonRow {
  id: string
  display_name: string
  hr_state: string | null
  position: string | null
  payroll_id: string | null
}

const STATE_LABEL: Record<string, { label: string; cls: string }> = {
  CANDIDATE: { label: 'Candidat', cls: 'lc-pill--info' },
  PRE_HIRE: { label: 'Pré-embauche', cls: 'lc-pill--info' },
  ONBOARDING: { label: 'Arrivée en cours', cls: 'lc-pill--info' },
  PENDING_VALIDATION: { label: 'À valider', cls: 'lc-pill--warning' },
  ACTIVE: { label: 'En poste', cls: 'lc-pill--ok' },
  SUSPENDED: { label: 'Suspendu', cls: 'lc-pill--warning' },
  LEAVING: { label: 'Départ en cours', cls: 'lc-pill--warning' },
  ENDED: { label: 'Sorti', cls: 'lc-pill--idle' },
  ARCHIVED: { label: 'Archivé', cls: 'lc-pill--idle' },
}

export function RhPeoplePage() {
  const [people, setPeople] = useState<PersonRow[] | null>(null)
  const [q, setQ] = useState('')
  const menu = useMenuPersonne()

  useEffect(() => {
    apiGet<{ people: PersonRow[] }>('/api/v1/rh/people')
      .then((d) => { if ('people' in d) setPeople(d.people) })
      .catch(() => setPeople([]))
  }, [])

  const filtered = useMemo(() => {
    if (!people) return null
    const n = q.trim().toLowerCase()
    return n ? people.filter((p) => p.display_name.toLowerCase().includes(n) || (p.position ?? '').toLowerCase().includes(n)) : people
  }, [people, q])

  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/rh" />
      <h1 className="lc-title"><Users size={20} aria-hidden="true" style={{ verticalAlign: -3, marginRight: 10 }} />Salariés</h1>
      <p className="lc-subtitle">Les dossiers du personnel. Ouvrez une fiche pour l’emploi, le temps et les permissions.</p>

      <div className="rh-toolbar">
        <label className="rh-search">
          <Search size={15} aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un salarié ou un poste…" aria-label="Rechercher un salarié" />
        </label>
        <span className="lc-hint">{filtered ? `${filtered.length} salarié${filtered.length > 1 ? 's' : ''}` : '…'}</span>
      </div>

      <section className="lc-panel gd-panel">
        <table className="lc-table rh-table">
          <thead>
            <tr>
              <th>Salarié</th>
              <th>Poste</th>
              <th>État</th>
              <th>Matricule</th>
            </tr>
          </thead>
          <tbody>
            {(filtered ?? []).map((p) => {
              const st = p.hr_state ? STATE_LABEL[p.hr_state] : null
              return (
                <tr key={p.id} className="lc-table__rowlink" onClick={() => { navigate(`/equipe/rh/salaries/${p.id}`) }}>
                  <td>
                    <Link href={`/equipe/rh/salaries/${p.id}`} className="rh-person" onClick={(e) => e.stopPropagation()}
                      {...menu.personne({ id: p.id, nom: p.display_name, matricule: p.payroll_id, accesLie: accesLieDeLigne(p) })}>
                      <span className="rh-initials" aria-hidden="true">{p.display_name.split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase()}</span>
                      {humanName(p.display_name)}
                    </Link>
                  </td>
                  <td>{p.position ?? <span className="rh-status-soft">Sans poste</span>}</td>
                  <td>{st ? <span className={`lc-pill ${st.cls}`}>{st.label}</span> : <span className="rh-status-soft"><span className="lc-dot lc-dot--idle" aria-hidden="true" /> Dossier à ouvrir</span>}</td>
                  <td>{p.payroll_id ? <code>{p.payroll_id}</code> : <span className="rh-status-soft">—</span>}</td>
                </tr>
              )
            })}
            {filtered && filtered.length === 0 ? (
              <tr><td colSpan={4} className="lc-hint" style={{ padding: 'var(--sp-5)' }}>Aucun salarié ne correspond.</td></tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </main>
  )
}
